import { useEffect, useState, useRef } from "react";

interface NavigationBlockerProps {
    onNavigationAttempt: () => Promise<boolean>;
    history?: any; // For React Router v5
    useBlocker?: any; // For React Router v6
    enabled?: boolean; // Add this prop to control blocking
}

export const useNavigationBlocker = ({
    onNavigationAttempt,
    history,
    useBlocker,
    enabled = true // Default to enabled
}: NavigationBlockerProps) => {
    // This state is primarily for the hook consumer to know if UI should indicate blocking.
    const [isBlocking, setIsBlocking] = useState(false);
    const [isEnabled, setIsEnabled] = useState(enabled);

    // --- React Router v6 Specific Refs ---
    // Ref to track if navigation has been approved and router should proceed.
    // This ref is used in the `useBlocker(WHEN_CONDITION)` call.
    const v6_hasApprovedNavigationRef = useRef(false);
    // Ref to track if an `onNavigationAttempt` is currently in progress for a v6 block.
    // This prevents calling onNavigationAttempt multiple times if the effect re-runs
    // while the async operation is still pending.
    const v6_isProcessingCurrentBlockRef = useRef(false);

    // --- React Router v5 Specific Refs ---
    // Ref to keep track of whether we're currently navigating (for v5)
    const v5_isNavigatingRef = useRef(false);

    // Function to disable the blocker
    const disable = () => {
        setIsEnabled(false);
        setIsBlocking(false);
    };

    // Function to enable the blocker
    const enable = () => {
        setIsEnabled(true);
    };

    // React Router v6 implementation
    if (useBlocker) {
        // The blocker is active if enabled AND (navigation hasn't been explicitly approved OR if we are currently processing a block).
        const blocker = useBlocker(
            isEnabled && (!v6_hasApprovedNavigationRef.current || v6_isProcessingCurrentBlockRef.current)
        );

        useEffect(() => {
            console.log(`useNavigationBlocker[v6]: Effect runs. blocker.state: ${blocker.state}, isEnabled: ${isEnabled}, v6_hasApprovedNavigationRef: ${v6_hasApprovedNavigationRef.current}, v6_isProcessingCurrentBlockRef: ${v6_isProcessingCurrentBlockRef.current}, isBlocking: ${isBlocking}`);

            if (!isEnabled) {
                // If disabled, ensure we're not blocking
                if (isBlocking) {
                    setIsBlocking(false);
                }
                return;
            }

            if (blocker.state === "blocked") {
                // Do not re-process onNavigationAttempt.
                if (v6_hasApprovedNavigationRef.current) {
                    console.log("useNavigationBlocker[v6]: Blocker is 'blocked', but navigation was already approved (v6_hasApprovedNavigationRef is true). Waiting for it to unblock/proceed fully.");
                    // Ensure UI shows blocking while proceeding.
                    if (!isBlocking) setIsBlocking(true);
                    return;
                }

                if (v6_isProcessingCurrentBlockRef.current) {
                    // An onNavigationAttempt is already in progress for this blocked navigation.
                    console.log("useNavigationBlocker[v6]: Blocker is 'blocked', but an onNavigationAttempt is already in progress (v6_isProcessingCurrentBlockRef is true). Ignoring this effect run for onNavigationAttempt.");
                    return;
                }

                // Start processing this specific block event.
                v6_isProcessingCurrentBlockRef.current = true;
                // Set isBlocking to true here to ensure UI reflects the blocking state immediately.
                // This also makes the `useBlocker` condition `!v6_hasApprovedNavigationRef.current || v6_isProcessingCurrentBlockRef.current` evaluate to true.
                if (!isBlocking) setIsBlocking(true);
                console.log(`useNavigationBlocker[v6]: blocker.state is 'blocked'. Invoking onNavigationAttempt().`);

                onNavigationAttempt()
                    .then(shouldProceed => {
                        console.log(`useNavigationBlocker[v6]: onNavigationAttempt() completed. shouldProceed: [${shouldProceed}]`);
                        if (shouldProceed) {
                            // Navigation is approved. Set ref to make useBlocker condition potentially false on next render
                            // (if v6_isProcessingCurrentBlockRef also becomes false).
                            v6_hasApprovedNavigationRef.current = true;
                            console.log(`useNavigationBlocker[v6]: Invoking blocker.proceed(). v6_hasApprovedNavigationRef set to true.`);
                            // UI's isBlocking state will be set to false when blocker.state becomes "unblocked".
                            // It should remain true during "proceeding".
                            if (blocker.state === "blocked") {
                                blocker.proceed();
                            }
                            else {
                                console.warn(`useNavigationBlocker[v6]: Blocker state was not 'blocked' (${blocker.state}) before attempting to proceed. This might indicate a race condition or an earlier resolution.`);
                            }
                        }
                        else {
                            // Navigation not approved, or save failed. Reset the blocker.
                            console.log("useNavigationBlocker[v6]: onNavigationAttempt returned false. Resetting blocker.");
                            if (blocker.state === "blocked" || blocker.state === "proceeding") {
                                blocker.reset();
                            }
                            setIsBlocking(false); // No longer blocking.
                            // v6_hasApprovedNavigationRef remains false because navigation was not approved.
                        }
                    })
                    .catch(error => {
                        console.error("useNavigationBlocker[v6]: Error during onNavigationAttempt():", error);
                        if (blocker.state === "blocked" || blocker.state === "proceeding") {
                            blocker.reset();
                        }
                        setIsBlocking(false); // No longer blocking due to error.
                        v6_hasApprovedNavigationRef.current = false; // Ensure this is reset on error.
                    })
                    .finally(() => {
                        // Finished this attempt to process the block.
                        // Allow future block events to be processed by onNavigationAttempt if they occur.
                        // console.log("useNavigationBlocker[v6]: onNavigationAttempt promise finally block. Resetting v6_isProcessingCurrentBlockRef.");
                        v6_isProcessingCurrentBlockRef.current = false;
                        // If navigation was approved, v6_hasApprovedNavigationRef is true.
                        // The useBlocker condition will then depend on !v6_hasApprovedNavigationRef.
                        // If not approved, v6_hasApprovedNavigationRef is false, useBlocker remains active if needed.
                        // Re-evaluate isBlocking after processing. If we are proceeding, it should stay true.
                        // If we reset, it should be false.
                        // The 'unblocked' or 'proceeding' state handlers will manage isBlocking.
                    });

            }
            else if (blocker.state === "unblocked") {
                // console.log("useNavigationBlocker[v6]: Blocker is 'unblocked'.");
                if (v6_hasApprovedNavigationRef.current) {
                    // If navigation was approved and has now completed (or blocker was reset after approval),
                    // reset the approval flag for future navigation attempts.
                    // console.log("useNavigationBlocker[v6]: Was approved (v6_hasApprovedNavigationRef is true), resetting it as blocker is now unblocked.");
                    v6_hasApprovedNavigationRef.current = false;
                }
                // v6_isProcessingCurrentBlockRef should be false here already due to finally, but as a safeguard:
                if (v6_isProcessingCurrentBlockRef.current) {
                    // console.log("useNavigationBlocker[v6]: Blocker unblocked, ensuring v6_isProcessingCurrentBlockRef is false.");
                    v6_isProcessingCurrentBlockRef.current = false;
                }
                if (isBlocking) { // Sync UI state if it was true.
                    // console.log("useNavigationBlocker[v6]: UI isBlocking was true, setting to false as blocker is unblocked.");
                    setIsBlocking(false);
                }
            }
            else if (blocker.state === "proceeding") {
                // console.log("useNavigationBlocker[v6]: Blocker is 'proceeding'.");
                // Ensure UI shows blocking while the router is actually navigating.
                if (!isBlocking) {
                    // console.log("useNavigationBlocker[v6]: Blocker proceeding and UI not blocking, setting isBlocking to true.");
                    setIsBlocking(true);
                }
                // v6_isProcessingCurrentBlockRef should be false here.
                // v6_hasApprovedNavigationRef should be true if we called proceed.
            }

        }, [blocker, onNavigationAttempt, isBlocking, isEnabled]); // Add isEnabled to dependencies

        return { isBlocking, disable, enable };
    }

    // React Router v5 implementation (remains largely the same)
    if (history) {
        useEffect(() => {
            let unblockFn: (() => void) | null = null;

            if (isEnabled && !v5_isNavigatingRef.current) { // Only block if enabled and not already programmatically navigating
                unblockFn = history.block((tx: any) => {
                    if (v5_isNavigatingRef.current) {
                        console.warn("useNavigationBlocker[v5]: Block triggered while v5_isNavigatingRef is true. Allowing navigation.");
                        return true;
                    }

                    setIsBlocking(true);
                    console.log(`useNavigationBlocker[v5]: Invoking onNavigationAttempt() for path: ${tx.pathname}`);

                    onNavigationAttempt()
                        .then(shouldProceed => {
                            console.log(`useNavigationBlocker[v5]: onNavigationAttempt() completed. shouldProceed: [${shouldProceed}]`);
                            if (shouldProceed) {
                                console.log(`useNavigationBlocker[v5]: Approved. Unblocking and navigating to ${tx.pathname}.`);
                                v5_isNavigatingRef.current = true;

                                if (unblockFn) {
                                    unblockFn();
                                    unblockFn = null;
                                }
                                history.push(tx.pathname);
                                setIsBlocking(false);

                                setTimeout(() => {
                                    v5_isNavigatingRef.current = false;
                                }, 100);
                            }
                            else {
                                console.log("useNavigationBlocker[v5]: Not approved. Navigation blocked.");
                                setIsBlocking(false);
                            }
                        })
                        .catch(error => {
                            console.error("useNavigationBlocker[v5]: Error during onNavigationAttempt():", error);
                            setIsBlocking(false);
                            v5_isNavigatingRef.current = false;
                        });

                    return false;
                });
            }

            return () => {
                if (unblockFn) {
                    unblockFn();
                }
            };
        }, [history, onNavigationAttempt, isEnabled]); // Add isEnabled to dependencies

        return { isBlocking, disable, enable };
    }

    console.error("useNavigationBlocker: Either 'history' (for v5) or 'useBlocker' (for v6) must be provided.");
    return { isBlocking: false, disable: () => {}, enable: () => {} };
};